import { useMemo } from 'react';
import { Hint } from '@/models/hints';
import { CreateCampaignStep, CreateCampaignStepToHintsMap } from '../types';
import { CREATE_CAMPAIGN_STEPS_HINTS } from '../constants';

type HintsMap<T extends CreateCampaignStep> = CreateCampaignStepToHintsMap[T] extends never 
  ? { hints: ReadonlyArray<undefined>; totalHints: 0 } 
  : Record<CreateCampaignStepToHintsMap[T], Hint<CreateCampaignStepToHintsMap[T]>> & {
      hints: ReadonlyArray<Hint<CreateCampaignStepToHintsMap[T]>>;
      totalHints: number;
    };

/**
 * The hook is strong typed if called useCreateCampaignStepHints('ConnectAudience')
 * Other if called as useCreateCampaignStepHints(step.id) care because he will suggest all hints.
 * In the second case make sure you only use the hint if available for that step.
 * 
 * @param stepId 
 * @returns 
 */
export const useCreateCampaignStepHints = <T extends CreateCampaignStep> (
  stepId: T
): HintsMap<T> => {
  return useMemo(() => {
    // Find the hints for the given step
    const stepHints = CREATE_CAMPAIGN_STEPS_HINTS.find(
      (item) => item.stepId === stepId
    );

    // If no hints exist for this step, return empty object with additional keys
    if (!stepHints || (stepHints.hints.length as number) === 0) {
      return { 
        hints: <ReadonlyArray<undefined>>[], 
        totalHints: 0 
      } as HintsMap<T>;
    }

    // Create a map with hint IDs as keys and hint objects as values
    const hintsMap = stepHints.hints.reduce((acc, hint) => {
      acc[hint.id] = hint as Hint<CreateCampaignStepToHintsMap[T]>;
      return acc;
    }, {} as HintsMap<T>);

    return {
      ...hintsMap,
      hints: stepHints.hints,
      totalHints: stepHints.hints.length
    };
    
  }, [stepId]);
};